import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { authService, userService, AuthUser as User, AuthResponse, TwoFactorResponse, OAuthProvider as AuthOAuthProvider } from '../../services';
import { isAuthError } from '../services/error-handling'; // Import isAuthError
import { sessionMonitorService } from '../services/core/session-monitor.service';
import { authInterceptorService } from '../services/core';
import teamSessionService from '../../services/TeamSessionService';

export type OAuthProvider = AuthOAuthProvider;

// Context interface
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  requiresTwoFactor: boolean;
  twoFactorUserId: string | null;
  login: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string, name?: string, dateOfBirth?: string) => Promise<any>;
  verifyTwoFactor: (token: string) => Promise<any>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (user: User) => void;
  updateUserProfile: (updates: Partial<User>) => void;
  refreshToken: () => Promise<boolean>;
  loginWithOAuth: (provider: OAuthProvider) => void;
  isEmailVerified: boolean;
  sendVerificationEmail: () => Promise<boolean>;
  verifyEmail: (token: string) => Promise<boolean>;
  sessionWarning: { show: boolean; minutesRemaining: number };
  dismissSessionWarning: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  requiresTwoFactor: false,
  twoFactorUserId: null,
  login: async () => ({}),
  register: async () => ({}),
  verifyTwoFactor: async () => ({}),
  logout: () => {},
  refreshUser: async () => {},
  updateUser: () => {},
  updateUserProfile: () => {},
  refreshToken: async () => false,
  loginWithOAuth: () => {},
  isEmailVerified: false,
  sendVerificationEmail: async () => false,
  verifyEmail: async () => false,
  sessionWarning: { show: false, minutesRemaining: 0 },
  dismissSessionWarning: () => {},
});

// Hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState<boolean>(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState<string | null>(null);
  const [oauthState, setOAuthState] = useState<string>('');
  const [sessionWarning, setSessionWarning] = useState<{ show: boolean; minutesRemaining: number }>({
    show: false,
    minutesRemaining: 0
  });
  const sessionWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user is authenticated and email is verified
  const isAuthenticated = !!user;
  const isEmailVerified = user?.isEmailVerified || false;

  // Generate a random state for OAuth to prevent CSRF attacks
  const generateOAuthState = (): string => {
    const randomState = Math.random().toString(36).substring(2, 15) +
                         Math.random().toString(36).substring(2, 15);
    return randomState;
  };

  // --- Authentication Error Handling and Redirect ---
  const navigate = useNavigate();

  const handleAuthError = useCallback(() => {
    console.warn('AuthContext: Authentication error detected, logging out and redirecting to login.');
    authService.clearAuthToken(); // Clear stored tokens
    setUser(null); // Clear user state
    setRequiresTwoFactor(false);
    setTwoFactorUserId(null);
    setSessionWarning({ show: false, minutesRemaining: 0 });

    // Stop session monitoring
    sessionMonitorService.stopMonitoring();

    // Define public routes that should not redirect to login
    const publicRoutes = [
      '/login',
      '/register',
      '/signup',
      '/forgot-password',
      '/reset-password',
      '/verify-email',
      '/features',
      '/pricing',
      '/about',
      '/help',
      '/status',
      '/legal',
    ];

    const isPublicRoute = publicRoutes.some(route =>
      window.location.pathname === route || window.location.pathname.startsWith(route + '/')
    );

    const isOAuthCallback = window.location.pathname.includes('/oauth/');
    const isPublicLink = window.location.pathname.startsWith('/public/');
    const isHomePage = window.location.pathname === '/';

    // Only redirect if not on a public page
    if (!isPublicRoute && !isOAuthCallback && !isPublicLink && !isHomePage) {
      navigate('/login');
    }
  }, [navigate]); // navigate is stable, but include for completeness

  // Effect to set the auth error handler on the authService
  useEffect(() => {
    console.log('AuthContext: Setting auth error handler on authService');
    authService.setAuthErrorHandler(handleAuthError);

    // Cleanup function to remove the handler when the component unmounts
    return () => {
      console.log('AuthContext: Removing auth error handler from authService');
      authService.setAuthErrorHandler(null);
    };
  }, [handleAuthError]); // Re-run if handleAuthError changes (it won't due to useCallback)
  // --- End Authentication Error Handling ---

  // --- Session Monitoring Setup ---
  useEffect(() => {
    // Initialize session monitor with auth interceptor
    sessionMonitorService.initialize(authInterceptorService);
    
    // Set up session monitor callbacks
    sessionMonitorService.onSessionExpired(() => {
      console.warn('AuthContext: Session expired');
      handleAuthError();
    });
    
    sessionMonitorService.onIdleTimeout(() => {
      console.warn('AuthContext: User idle timeout reached');
      sessionMonitorService.broadcastAction('logout');
      handleAuthError();
    });
    
    sessionMonitorService.onSessionWarning((minutesRemaining) => {
      console.log(`AuthContext: Session warning - ${minutesRemaining} minutes remaining`);
      setSessionWarning({ show: true, minutesRemaining });
      
      // Auto-hide warning after 30 seconds
      if (sessionWarningTimeoutRef.current) {
        clearTimeout(sessionWarningTimeoutRef.current);
      }
      sessionWarningTimeoutRef.current = setTimeout(() => {
        setSessionWarning({ show: false, minutesRemaining: 0 });
      }, 30000);
    });
    
    sessionMonitorService.onActivityResumed(() => {
      console.log('AuthContext: User activity resumed');
      setSessionWarning({ show: false, minutesRemaining: 0 });
      
      // Refresh token if needed when user resumes activity
      if (authService.hasValidAuthToken()) {
        refreshToken();
      }
    });
    
    return () => {
      // Cleanup timeout on unmount
      if (sessionWarningTimeoutRef.current) {
        clearTimeout(sessionWarningTimeoutRef.current);
      }
    };
  }, [handleAuthError]);
  // --- End Session Monitoring Setup ---


  // Load user on initial render
  useEffect(() => {
    const loadUser = async () => {
      // Check token and validate format
      const hasToken = authService.hasValidAuthToken();
      
      if (hasToken) {
        try {
          console.log('AuthContext: Loading user profile with token');
          const user = await authService.getProfile();
          console.log('AuthContext: User profile loaded successfully', user);
          setUser(user);
          
          // Start session monitoring when user is loaded
          sessionMonitorService.startMonitoring();
        } catch (error: any) {
          // Use the isAuthError utility to check for auth errors
          console.error('AuthContext: Error loading user profile:', error);
          if (isAuthError(error)) { // Use the imported utility
            console.warn('AuthContext: Token invalid, triggering auth error handler');
            handleAuthError(); // Use the centralized handler
          } else {
            // For non-auth errors, keep the token but still set user as null
            console.warn('AuthContext: Non-auth error, keeping token but user remains null');
          }
        }
      } else {
        console.log('AuthContext: No valid token found in localStorage');
        // Clear any potentially invalid token
        authService.clearAuthToken();
      }
      setIsLoading(false);
    };

    loadUser();
    
    // Generate a new OAuth state and store it in session storage
    const newState = generateOAuthState();
    setOAuthState(newState);
    sessionStorage.setItem('oauthState', newState);
  }, [handleAuthError]); // Add handleAuthError to dependency array

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      
      if ('requiresTwoFactor' in response && response.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setTwoFactorUserId(response.userId);
        return { requiresTwoFactor: true, user: response.user };
      }
      
      // Response is already typed as AuthResponse
      const authResponse = response as AuthResponse;
      setUser(authResponse.user);
      setRequiresTwoFactor(false);
      setTwoFactorUserId(null);
      
      // Start session monitoring after successful login
      sessionMonitorService.startMonitoring();
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Register function
  const register = async (email: string, password: string, name?: string, dateOfBirth?: string) => {
    try {
      const response = await authService.register(email, password, name, dateOfBirth);
      setUser(response.user);

      // Start session monitoring after successful registration
      sessionMonitorService.startMonitoring();

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Verify 2FA token
  const verifyTwoFactor = async (token: string) => {
    if (!twoFactorUserId) {
      throw new Error('No two-factor authentication in progress');
    }

    try {
      const response = await authService.verifyTwoFactor(twoFactorUserId, token);
      setUser(response.user);
      setRequiresTwoFactor(false);
      setTwoFactorUserId(null);
      
      // Start session monitoring after successful 2FA verification
      sessionMonitorService.startMonitoring();
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    // CRITICAL: Always clear tokens locally for security, even if backend calls fail
    // This is the safety valve - user must be logged out locally immediately

    let backendLogoutSuccessful = false;

    try {
      // Attempt to logout via backend (ends all sessions and cleans up database)
      await authService.logout();
      backendLogoutSuccessful = true;
      console.log('[AuthContext] Backend logout successful - all sessions ended');
    } catch (error) {
      // Log error but don't block local logout
      console.error('[AuthContext] Backend logout failed, proceeding with local logout:', error);
      // This is expected to work now with the new /auth/logout endpoint
    }

    // ALWAYS clear local authentication state (security priority)
    authService.clearAuthToken();
    setUser(null);
    setRequiresTwoFactor(false);
    setTwoFactorUserId(null);
    setSessionWarning({ show: false, minutesRemaining: 0 });

    // Stop session monitoring and broadcast logout to other tabs
    sessionMonitorService.broadcastAction('logout');
    sessionMonitorService.stopMonitoring();

    // Navigate to login page
    navigate('/login');

    // Show appropriate message to user
    if (backendLogoutSuccessful) {
      console.log('[AuthContext] Logout complete');
    } else {
      console.warn('[AuthContext] Logged out locally, but backend session cleanup may need time to complete');
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const user = await authService.getProfile();
      setUser(user);
    } catch (error) {
      // If token is invalid, logout
      // Use the centralized handler
      if (isAuthError(error)) {
         handleAuthError();
      } else {
        console.error('AuthContext: Error refreshing user profile (non-auth error):', error);
      }
    }
  };

  // Update user data directly without API call
  const updateUser = (user: User) => {
    setUser(user);
  };

  // --- Profile Autosave Logic ---
  // Track if this is initial load to prevent autosave on mount
  const isInitialUserLoadRef = useRef(true);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  // Store pending updates in a ref to batch them together
  const pendingUpdatesRef = useRef<Partial<User>>({});

  // Update user profile with optimistic updates and automatic API sync
  const updateUserProfile = useCallback((updates: Partial<User>) => {
    if (!user) return;

    console.log('[AuthContext] updateUserProfile called with updates:', updates);

    // OPTIMIZATION: Accumulate updates in a ref to batch multiple changes
    pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates };

    // Optimistic update - immediately update local state
    const updatedUser = { ...user, ...pendingUpdatesRef.current };
    setUser(updatedUser);

    // Clear any existing autosave timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    // Debounce API sync (3 seconds after last change)
    autosaveTimeoutRef.current = setTimeout(() => {
      if (isSavingRef.current) {
        console.log('[AuthContext] Save already in progress, skipping');
        return;
      }

      // Get all accumulated updates
      const allUpdates = { ...pendingUpdatesRef.current };
      pendingUpdatesRef.current = {}; // Clear pending updates

      isSavingRef.current = true;
      console.log('[AuthContext] Syncing profile to API:', allUpdates);

      userService.updateProfile(allUpdates)
        .then(response => {
          console.log('[AuthContext] Profile synced successfully');
          // Update with server response to ensure consistency
          const userData = response.user || response;
          setUser(prev => prev ? { ...prev, ...userData } : null);
        })
        .catch(error => {
          console.error('[AuthContext] Failed to sync profile:', error);
          // Revert optimistic update on error
          setUser(user);
          // TODO: Show error notification to user
        })
        .finally(() => {
          isSavingRef.current = false;
        });
    }, 3000);
  }, [user]);

  // Cleanup autosave timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  // Mark initial load complete after first user load
  useEffect(() => {
    if (user && isInitialUserLoadRef.current) {
      isInitialUserLoadRef.current = false;
      console.log('[AuthContext] Initial user load complete');
    }
  }, [user]);
  // --- End Profile Autosave Logic ---

  // OAuth login function
  const loginWithOAuth = async (provider: OAuthProvider) => {
    try {
      // Store the current URL to redirect back to after login (if needed)
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      
      // Get the authorization URL from the API
      const { authUrl } = await authService.getOAuthUrl(provider);
      
      // Redirect to the authorization URL
      window.location.href = authUrl;
    } catch (error) {
      console.error(`Error initiating ${provider} OAuth flow:`, error);
      // You might want to show an error message to the user here
    }
  };

  // Send email verification
  const sendVerificationEmail = async (): Promise<boolean> => {
    try {
      const response = await authService.sendVerificationEmail();
      return response.success;
    } catch (error) {
      console.error('Failed to send verification email:', error);
      return false;
    }
  };

  // Verify email with token
  const verifyEmail = async (token: string): Promise<boolean> => {
    try {
      // Since we don't have the exact endpoint structure from the new gateway,
      // we'll use the closest endpoint we have - which is verify-email in the user routes
      const response = await authService.verifyEmail(token);
      if (response.success && response.user) {
        // Update the user with verified email status
        setUser(response.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to verify email:', error);
      return false;
    }
  };

  // Manually refresh the access token
  const refreshToken = async (): Promise<boolean> => {
    try {
      const newToken = await authService.refreshAccessToken();
      if (newToken) {
        console.log('AuthContext: Token refreshed successfully');
        // Reschedule session warning after token refresh
        sessionMonitorService.triggerActivity();
        return true;
      } else {
        console.warn('AuthContext: Token refresh failed');
        handleAuthError();
        return false;
      }
    } catch (error) {
      console.error('AuthContext: Error refreshing token:', error);
      handleAuthError();
      return false;
    }
  };

  // Dismiss session warning
  const dismissSessionWarning = () => {
    setSessionWarning({ show: false, minutesRemaining: 0 });
    if (sessionWarningTimeoutRef.current) {
      clearTimeout(sessionWarningTimeoutRef.current);
      sessionWarningTimeoutRef.current = null;
    }
  };

  // Context value
  const value = {
    user,
    isLoading,
    isAuthenticated,
    requiresTwoFactor,
    twoFactorUserId,
    login,
    register,
    verifyTwoFactor,
    logout,
    refreshUser,
    updateUser,
    updateUserProfile,
    refreshToken,
    loginWithOAuth,
    isEmailVerified,
    sendVerificationEmail,
    verifyEmail,
    sessionWarning,
    dismissSessionWarning,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
